// Geolocation utilities for map and nearby search

const STORAGE_KEY = 'roomhora_location_permission';

/**
 * Check if location permission was previously granted/denied
 */
export function getStoredLocationPreference() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store location preference
 */
export function setStoredLocationPreference(preference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Check browser geolocation permission status
 */
export async function checkGeolocationPermission() {
  if (!navigator.permissions) {
    return 'unknown';
  }
  
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state; // 'granted', 'denied', or 'prompt'
  } catch {
    return 'unknown';
  }
}

/**
 * Get current position with options
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000, // Cache for 1 minute
      ...options,
    };
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        let message = 'Unable to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            message = 'Location request timed out';
            break;
        }
        reject(new Error(message));
      },
      defaultOptions
    );
  });
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 * Returns distance in kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg) {
  return deg * (Math.PI / 180);
}

/**
 * Format distance for display
 */
export function formatDistance(km) {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  if (km < 10) {
    return `${km.toFixed(1)} km`;
  }
  return `${Math.round(km)} km`;
}

/**
 * Get bounds for a radius around a point
 */
export function getBoundsForRadius(lat, lng, radiusKm) {
  // Approximate degrees per km
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos(toRad(lat)));
  
  return {
    north: lat + latDelta,
    south: lat - latDelta,
    east: lng + lngDelta,
    west: lng - lngDelta,
  };
}

/**
 * Default map centers for major cities (fallback when no location)
 */
export const DEFAULT_LOCATIONS = {
  'New York': { lat: 40.7128, lng: -74.0060 },
  'London': { lat: 51.5074, lng: -0.1278 },
  'Tokyo': { lat: 35.6762, lng: 139.6503 },
  'Singapore': { lat: 1.3521, lng: 103.8198 },
  'Dubai': { lat: 25.2048, lng: 55.2708 },
  'Sydney': { lat: -33.8688, lng: 151.2093 },
  'Paris': { lat: 48.8566, lng: 2.3522 },
  'Berlin': { lat: 52.5200, lng: 13.4050 },
  'Mumbai': { lat: 19.0760, lng: 72.8777 },
  'Kathmandu': { lat: 27.7172, lng: 85.3240 },
  'Bangkok': { lat: 13.7563, lng: 100.5018 },
  'Hong Kong': { lat: 22.3193, lng: 114.1694 },
};

/**
 * Get a reasonable default location based on timezone
 */
export function getDefaultLocationByTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Map timezones to cities
    const tzMap = {
      'America/New_York': 'New York',
      'America/Los_Angeles': 'New York',
      'America/Chicago': 'New York',
      'Europe/London': 'London',
      'Europe/Paris': 'Paris',
      'Europe/Berlin': 'Berlin',
      'Asia/Tokyo': 'Tokyo',
      'Asia/Singapore': 'Singapore',
      'Asia/Dubai': 'Dubai',
      'Asia/Kolkata': 'Mumbai',
      'Asia/Kathmandu': 'Kathmandu',
      'Asia/Bangkok': 'Bangkok',
      'Asia/Hong_Kong': 'Hong Kong',
      'Australia/Sydney': 'Sydney',
    };
    
    const city = tzMap[tz];
    if (city && DEFAULT_LOCATIONS[city]) {
      return DEFAULT_LOCATIONS[city];
    }
  } catch {
    // Ignore errors
  }
  
  // Default to New York
  return DEFAULT_LOCATIONS['New York'];
}

/**
 * Nearby radius options
 */
export const RADIUS_OPTIONS = [
  { value: 1, label: '1 km' },
  { value: 3, label: '3 km' },
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
];
